'use client';

import { useIsHydrated } from './use-is-hydrated';

/**
 * The time zone a client component should format an instant (a timestamptz)
 * in, without breaking hydration.
 *
 * The server renders in UTC and has no way to know the visitor's zone, so a
 * client component that formats `created_at` in the runtime's local zone
 * prints one date in the server HTML and another in the browser whenever the
 * two zones straddle midnight — "Oct 22" on the server, "Oct 23" in Sydney.
 * React treats that text mismatch as a hydration failure and re-renders the
 * whole page on the client (BALLEE-HZ / BAL-1027).
 *
 * Returns `'UTC'` for the server render AND the hydration render, so both
 * print the same text, then `undefined` (the runtime's local zone) once
 * hydration is done; React re-renders with the local date at that point.
 *
 * Pass the result as the `timeZone` option of `Intl.DateTimeFormat` /
 * `toLocaleDateString`. Calendar dates (`date` columns) are not instants and
 * must not go through this: `parseLocalDate` already reads them the same way
 * in every runtime.
 */
export function useDisplayTimeZone(): 'UTC' | undefined {
  return useIsHydrated() ? undefined : 'UTC';
}
